import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Confirm, Loading, useDataProvider, useDelete, useNotify } from 'react-admin';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { usePrevious } from '@app/hooks/usePrevious';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { formatDeleteMessageWithLinks } from '@app/utils/taxonomyUtils';
import AddIcon from '@mui/icons-material/Add';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Button from '@mui/material/Button';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { LinksDeleteMessage } from './LinksDeleteMessage';
import { TaxonomyDetails } from './TaxonomyDetails';
import { TaxonomyTree } from './TaxonomyTree';

export const ACTION_MODE = {
  CREATE: 'create',
  EDIT: 'edit',
  DEFAULT: 'default',
  REORDER: 'reorder',
};

const DEFAULT_DELETE_MESSAGE = 'Are you sure you want to delete this taxonomy?';

export const ListTaxonomies = () => {
  const [handleError] = useErrorHandling();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const ability = useAbility();

  const [selectedTaxonomy, setSelectedTaxonomy] = useState<GenericObject | null>(null);
  const [taxonomies, setTaxonomies] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [actionMode, setActionMode] = useState<string | null>(null);
  const [deleteOne, { isLoading: isDeleteLoading }] = useDelete('taxonomies', undefined, {
    onSettled: () => {
      fetchTaxonomies();
    },
    onSuccess: () => {
      fetchTaxonomies();
      handleChangeActionMode(ACTION_MODE.DEFAULT);
      handleShowInfo(null);
      setDeletedParentAndChildId({
        parent_id: deletingTaxonomy?.parent_category_id,
        child_id: deletingTaxonomy?.id,
      });
    },
    onError: (error: any) => {
      handleError(error);
    },
  });
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [deletingTaxonomy, setDeletingTaxonomy] = useState<GenericObject | null>(null);
  const [deletedParentAndChildId, setDeletedParentAndChildId] = useState<GenericObject | null>(null);
  const [shouldExpandAll, setShouldExpandAll] = useState<boolean | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<ReactNode | string>('');

  const prevState = usePrevious({ actionMode });

  const fetchTaxonomies = useCallback(() => {
    dataProvider
      .getList('categories', { pagination: { page: 0, perPage: 1000 }, sort: { field: '', order: '' }, filter: '' })
      .then(({ data }: any) => {
        setTaxonomies(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [dataProvider]);

  //Fetch taxonomies on mount
  useEffect(() => {
    fetchTaxonomies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevState && prevState?.actionMode !== actionMode && actionMode === ACTION_MODE.DEFAULT) {
      fetchTaxonomies();
    }
  }, [actionMode, prevState, fetchTaxonomies]);

  const handleShowInfo = (taxonomy: GenericObject | null) => {
    handleChangeActionMode(ACTION_MODE.DEFAULT);
    setSelectedTaxonomy(taxonomy);
  };

  const handleChangeActionMode = (mode: string) => {
    setActionMode(mode);
  };

  const onDelete = async (row: GenericObject) => {
    try {
      const response = await dataProvider.fetchLinks('taxonomies', row.id);
      const links = response?.data;

      if (links.children) {
        notify(
          'This topic cannot be deleted. In order to delete this topic, you must first delete all of its child topics.',
          { type: 'error' }
        );
        return;
      }

      if (links.topicDrivenRails) {
        notify('This topic is associated with a topic-driven rail. Remove the topic from all rails and try again.', {
          type: 'error',
        });
        return;
      }

      const messageWithLinks = formatDeleteMessageWithLinks(links);

      setDeleteMessage(
        messageWithLinks ? <LinksDeleteMessage messageWithLinks={messageWithLinks} /> : DEFAULT_DELETE_MESSAGE
      );

      setDeletingTaxonomy(row);
      setIsDeleteConfirmationOpen(true);
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteOne('taxonomies', { id: deletingTaxonomy?.id as number });
    } catch (error: any) {
      handleError(error);
    } finally {
      handleDialogClose();
    }
  };

  const handleDialogClose = () => {
    setDeletingTaxonomy(null);
    setIsDeleteConfirmationOpen(false);
  };

  const onAddNew = () => {
    handleChangeActionMode(ACTION_MODE.CREATE);
  };

  const shouldDisableAddNew = () => {
    //Add New should be disabled when the user is trying to create a taxonomy under level 4 (max level)
    return selectedTaxonomy?.level === 4;
  };

  const onExpandAll = () => {
    handleChangeShouldExpandAll(true);
  };

  const onCollapseAll = () => {
    handleChangeShouldExpandAll(false);
  };

  const handleChangeShouldExpandAll = useCallback((value: boolean | null) => {
    setShouldExpandAll(value);
  }, []);

  if (loading) return <Loading />;

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ padding: '20px 0px', display: 'flex' }}>
        <div style={{ width: '30%' }}>
          <div style={{ padding: '17px 18px 30px 17px', display: 'flex', width: '100%', alignItems: 'center' }}>
            <div style={{ width: '100%' }}>
              {ability.can(CASLAction.WRITE, 'taxonomies') && (
                <Button
                  size="small"
                  variant="contained"
                  data-cy="addNew"
                  disabled={shouldDisableAddNew()}
                  onClick={onAddNew}
                  startIcon={<AddIcon />}>
                  ADD NEW
                </Button>
              )}
            </div>
            <div style={{ width: '100%', display: 'flex' }}>
              <Button size="small" onClick={onExpandAll} startIcon={<UnfoldMoreIcon />}>
                Expand
              </Button>
              <Button data-cy="collapse" size="small" onClick={onCollapseAll} startIcon={<UnfoldLessIcon />}>
                Collapse
              </Button>
            </div>
          </div>
          <div style={{ maxHeight: '750px', overflow: 'auto' }}>
            {taxonomies.map((taxonomy: GenericObject, index: number) => (
              <TaxonomyTree
                index={index}
                handleChangeShouldExpandAll={handleChangeShouldExpandAll}
                shouldExpandAll={shouldExpandAll}
                deletedParentAndChildId={deletedParentAndChildId}
                actionMode={actionMode}
                key={taxonomy.id as number}
                taxonomy={taxonomy}
                handleShowInfo={handleShowInfo}
                selectedTaxonomy={selectedTaxonomy}
              />
            ))}
          </div>
        </div>
        <div style={{ width: '70%' }}>
          <TaxonomyDetails
            onDelete={onDelete}
            handleShowInfo={handleShowInfo}
            actionMode={actionMode}
            handleChangeActionMode={handleChangeActionMode}
            selectedTaxonomy={selectedTaxonomy}
          />
        </div>
      </div>

      <Confirm
        isOpen={isDeleteConfirmationOpen}
        loading={isDeleteLoading}
        title={`Delete taxonomy?`}
        content={deleteMessage}
        onConfirm={handleConfirmDelete}
        cancel="Cancel"
        confirm="Delete"
        onClose={handleDialogClose}
      />
    </DndProvider>
  );
};
